import React, { forwardRef, useCallback } from "react";

// --- Hooks ---
import { useTiptapEditor } from "app/components/TextEditor/hooks/use-tiptap-editor";

// --- Tiptap UI ---
import type { UseInsertTableConfig } from "app/components/TextEditor/components/tiptap-ui/insert-table-button/use-insert-table";
import { useInsertTable } from "app/components/TextEditor/components/tiptap-ui/insert-table-button/use-insert-table";

// --- UI Primitives ---
import type { ButtonProps } from "app/components/TextEditor/components/tiptap-ui-primitive/button";
import { Button } from "app/components/TextEditor/components/tiptap-ui-primitive/button";
import { Badge } from "app/components/TextEditor/components/tiptap-ui-primitive/badge";
import { parseShortcutKeys } from "app/components/TextEditor/lib/tiptap-utils";

type IconProps = React.SVGProps<SVGSVGElement>;
type IconComponent = ({ className, ...props }: IconProps) => React.ReactElement;

export interface InsertTableButtonProps extends Omit<ButtonProps, "type"> {
  editor?: UseInsertTableConfig["editor"];
  rows?: number;
  cols?: number;
  withHeaderRow?: boolean;
  hideWhenUnavailable?: boolean;
  onInserted?: () => void;
  /**
   * Optional text to display alongside the icon.
   */
  text?: string;
  /**
   * Optional show shortcut keys in the button.
   * @default false
   */
  showShortcut?: boolean;
  /**
   * Optional custom icon component to render instead of the default.
   */
  icon?: React.MemoExoticComponent<IconComponent> | React.FC<IconProps>;
}

export function InsertTableShortcutBadge({
  shortcutKeys,
}: {
  shortcutKeys?: string;
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}
/**
 * Button component for inserting a table in a Tiptap editor.
 *
 * For custom button implementations, use the `useInsertTable` hook instead.
 */
export const InsertTableButton = forwardRef<
  HTMLButtonElement,
  InsertTableButtonProps
>(
  (
    {
      editor: providedEditor,
      text,
      rows,
      cols,
      withHeaderRow,
      hideWhenUnavailable = false,
      onInserted,
      showShortcut = false,
      onClick,
      icon: CustomIcon,
      children,
      ...buttonProps
    },
    ref,
  ) => {
    const { editor } = useTiptapEditor(providedEditor);
    const {
      isVisible,
      handleInsertTable,
      label,
      canInsert,
      isActive,
      Icon,
      shortcutKeys,
    } = useInsertTable({
      editor,
      rows,
      cols,
      withHeaderRow,
      hideWhenUnavailable,
      onInserted,
    });

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        onClick?.(event);
        if (event.defaultPrevented) return;
        handleInsertTable();
      },
      [handleInsertTable, onClick],
    );

    if (!isVisible) {
      return null;
    }

    const RenderIcon = CustomIcon ?? Icon;

    return (
      <Button
        type="button"
        disabled={!canInsert}
        variant="ghost"
        data-active-state={isActive ? "on" : "off"}
        data-disabled={!canInsert}
        role="button"
        tabIndex={-1}
        aria-label={label}
        aria-pressed={isActive}
        tooltip={label}
        onClick={handleClick}
        {...buttonProps}
        ref={ref}
      >
        {children ?? (
          <>
            <RenderIcon className="tiptap-button-icon" />
            {text && <span className="tiptap-button-text">{text}</span>}
            {showShortcut && (
              <InsertTableShortcutBadge shortcutKeys={shortcutKeys} />
            )}
          </>
        )}
      </Button>
    );
  },
);

InsertTableButton.displayName = "InsertTableButton";
